/* eslint-disable prettier/prettier */
/* eslint-disable no-undef */
import { IUser } from "types/user.type";
import api from "./ApiService";
import TokenService from "./TokenService";
import jwtDecode from "jwt-decode";

let refreshTimeout: any = null;
let refreshPromise: Promise<any> | null = null;
const REFRESH_LOCK_KEY = "AuthService.refreshLock";
const LOCK_TIMEOUT_MS = 30 * 1000;
let refreshLockValue: string | null = null;
let listenersInstalled = false;

const generateLockId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2)}`;

const acquireRefreshLock = (): boolean => {
  try {
    const now = Date.now();
    const existing = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null",
    );
    if (existing && existing.ts && existing.ts + LOCK_TIMEOUT_MS > now) {
      return false;
    }
    const lock = { id: generateLockId(), ts: now };
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
    refreshLockValue = lock.id;
    return true;
  } catch (e) {
    return false;
  }
};

const releaseRefreshLock = () => {
  try {
    const existing = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null",
    );
    if (existing?.id === refreshLockValue) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch (e) {
    // ignore
  } finally {
    refreshLockValue = null;
  }
};

const clearScheduledRefresh = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
};

const getTokenStringFromResponse = (response: any) => {
  const obj = response?.data ?? response ?? {};
  return (
    obj?.token ||
    obj?.Token ||
    obj?.accessToken ||
    obj?.access_token ||
    obj?.access ||
    null
  );
};

const getRefreshTokenFromStored = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    const obj = stored?.data ?? stored ?? {};
    return obj?.refreshToken || obj?.refresh_token || obj?.RefreshToken || null;
  } catch {
    return null;
  }
};

const scheduleRefresh = (tokenStr: string | null) => {
  clearScheduledRefresh();
  if (!tokenStr) return;

  // Install cross-tab and visibility listeners once to handle tab sleep/wake
  if (!listenersInstalled && typeof window !== "undefined") {
    const install = () => {
      try {
        window.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            const stored = JSON.parse(
              localStorage.getItem("LoggedInUser") || "null",
            );
            const tok = getTokenStringFromResponse(
              stored?.data ?? stored ?? {},
            );
            scheduleRefresh(tok);
          }
        });

        window.addEventListener("focus", () => {
          const stored = JSON.parse(
            localStorage.getItem("LoggedInUser") || "null",
          );
          const tok = getTokenStringFromResponse(stored?.data ?? stored ?? {});
          scheduleRefresh(tok);
        });

        window.addEventListener("storage", (e: StorageEvent) => {
          if (e.key === "LoggedInUser") {
            try {
              const newStored = JSON.parse(e.newValue || "null");
              const tok = getTokenStringFromResponse(
                newStored?.data ?? newStored ?? {},
              );
              scheduleRefresh(tok);
            } catch {
              // ignore
            }
          }
        });
      } catch (e) {
        // ignore
      }
    };

    install();
    listenersInstalled = true;
  }

  let payload: any;
  try {
    payload = jwtDecode(tokenStr);
  } catch (e) {
    console.warn("Invalid token decode", e);
    return;
  }

  if (!payload?.exp) return;

  const expiresAtMs = payload.exp * 1000;
  const now = Date.now();

  if (expiresAtMs <= now) {
    console.warn("Token already expired");
    return;
  }

  const lifetime = expiresAtMs - payload.iat * 1000;

  const buffer = Math.min(lifetime * 0.2, 2 * 60 * 1000);

  const refreshAt = expiresAtMs - buffer;
  const delay = refreshAt - now;

  const doRefresh = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      // Try to acquire a cross-tab lock so only one tab performs the refresh
      if (!acquireRefreshLock()) {
        // Another tab is refreshing — return and rely on storage event to pick up new token
        return null;
      }

      try {
        const refreshTok = getRefreshTokenFromStored();
        if (!refreshTok) return null;

        const data = await refreshToken(refreshTok);
        const responsePayload = data?.data ?? data ?? {};

        const updatedToken = getTokenStringFromResponse(responsePayload);
        if (!updatedToken) {
          console.warn("No token in refresh response");
          return null;
        }

        let newPayload: any;
        try {
          newPayload = jwtDecode(updatedToken);
        } catch {
          console.warn("Invalid refreshed token");
          return null;
        }

        if (!newPayload?.exp || newPayload.exp * 1000 <= Date.now()) {
          console.warn("Expired refreshed token");
          return null;
        }

        const stored = JSON.parse(
          localStorage.getItem("LoggedInUser") || "null",
        );

        if (stored) {
          if (stored.data && typeof stored.data === "object") {
            stored.data = { ...stored.data, ...responsePayload };
          } else {
            Object.assign(stored, responsePayload);
          }

          TokenService.setUser(stored);
          scheduleRefresh(updatedToken);
        }

        return responsePayload;
      } catch (e) {
        console.error("Token refresh failed:", e);
        throw e;
      } finally {
        releaseRefreshLock();
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  const triggerRefresh = () => {
    if (!refreshPromise) {
      void doRefresh();
    }
  };

  if (delay <= 0) {
    triggerRefresh();
    return;
  }

  const MIN_DELAY = 1000;

  refreshTimeout = setTimeout(triggerRefresh, Math.max(delay, MIN_DELAY));
};

const login = (username: string | any, password: string | any) => {
  return api
    .post<IUser>(process.env.REACT_APP_API_BASE_URL + "/auth/login", {
      username,
      password,
    })
    .then((response: any) => {
      if (response) {
        TokenService.setUser(response);
        const tokenStr = getTokenStringFromResponse(response);
        scheduleRefresh(tokenStr);
      }
      return response.data;
    });
};

const userReg = (id: string | any, password: string | any) => {
  return api
    .put<IUser>(
      process.env.REACT_APP_API_BASE_URL + `/users/${id}/complete-registration`,
      { password },
    )
    .then((response: any) => response.data);
};

const passwordReset = (data: string | any) => {
  return api
    .post<IUser>(
      process.env.REACT_APP_API_BASE_URL + "/users/forgot-password",
      { userEmail: data },
    )
    .then((response: any) => response.data);
};

const resetPassword = (
  email: string | any,
  password: string | any,
  id: number | any,
) => {
  return api
    .post<IUser>(
      process.env.REACT_APP_API_BASE_URL + `/users/${id}/reset-password`,
      {
        userEmail: email,
        newPassword: password,
      },
    )
    .then((response: any) => {
      try {
        const stored = JSON.parse(
          localStorage.getItem("LoggedInUser") || "null",
        );

        const payload = response?.data ?? response ?? {};
        const updatedToken = getTokenStringFromResponse(payload);

        if (!updatedToken) return response.data;

        if (stored) {
          if (stored.data && typeof stored.data === "object") {
            stored.data = { ...stored.data, ...payload };
          } else if (typeof stored === "object") {
            Object.assign(stored, payload);
          }

          TokenService.setUser(stored);
          scheduleRefresh(updatedToken);
        }
      } catch {
        // ignore
      }

      return response.data;
    });
};

const refreshToken = (token: string | any) => {
  return api
    .post<any>(process.env.REACT_APP_API_BASE_URL + `/auth/refresh`, {
      refreshToken: token,
    })
    .then((response: any) => response.data);
};

const updatePassword = (
  oldPassword: string | any,
  newPassword: string | any,
  id: number | any,
) => {
  return api
    .put<IUser>(
      process.env.REACT_APP_API_BASE_URL + `/users/${id}/update-password`,
      {
        oldPassword,
        newPassword,
      },
    )
    .then((response: any) => response.data);
};

const AuthService = {
  login,
  userReg,
  passwordReset,
  resetPassword,
  updatePassword,
  refreshToken,
  scheduleRefresh,
};

export default AuthService;
